<template>
  <label :class="style({ isChecked, isError, withBorder, disabled: props.disabled })">
    <input class="invisible h-0 w-0" type="radio" :checked="isChecked" v-bind="$attrs" :data-testid="option.value" :disabled="props.disabled" :value="option.value" @input="updateValue" />
    <span :class="radioStyle({ isChecked, disabled: props.disabled })"></span>
    <span>{{ option.label }}</span>
  </label>
</template>

<script setup lang="ts">
import { tv } from 'tailwind-variants';

import type { Option } from '@/models/selectBox';

interface Props {
  modelValue?: string;
  option: Option;
  disabled?: boolean;
  isError?: boolean;
  withBorder?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  isError: false,
  withBorder: true,
  disabled: false,
});

const isChecked = computed(() => props.modelValue === props.option.value);

interface Emits {
  (event: 'update:modelValue', value: string): void;
}

const emits = defineEmits<Emits>();

const updateValue = (e: Event) => {
  emits('update:modelValue', (e.target as HTMLInputElement).value);
};

/**
 * styles
 */
const style = tv({
  base: 'radio box-border flex h-8 w-full cursor-pointer appearance-none items-center bg-white px-2 text-sm text-black1 outline-none transition-all duration-100 ease-in-out md:px-4',
  variants: {
    withBorder: {
      true: 'border-[1px] border-solid border-gray2',
      false: 'border-none',
    },
    isChecked: {
      true: 'border-black1 text-black1',
      false: '',
    },
    disabled: {
      true: 'text-gray2',
      false: 'text-black1',
    },
    isError: {
      true: 'border-red1 bg-red2 text-red1',
      false: '',
    },
  },
});

const radioStyle = tv({
  base: 'relative mr-2 box-border inline-block h-4 w-4 min-w-4 rounded-[50%] border-[1px] border-solid border-black1 text-black1',
  variants: {
    isChecked: {
      true: 'bg-black1',
      false: 'bg-white',
    },
    disabled: {
      true: 'border-gray1',
      false: '',
    },
  },
});
</script>

<style scoped lang="scss">
.radio {
  -webkit-tap-highlight-color: transparent;
}
</style>
